import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Target, Play, ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { playChessMoveSound } from '@/lib/chessSounds';
import QuizPuzzleRunner from '@/components/studies/QuizPuzzleRunner';
import QuizPlayComputerRunner from '@/components/studies/QuizPlayComputerRunner';
import QuizCaptureRunner from '@/components/studies/QuizCaptureRunner';
import QuizMultipleChoiceRunner from '@/components/studies/QuizMultipleChoiceRunner';
import QuizResult from '@/components/studies/QuizResult';

// FASE QUIZZES · PASO 4A — Pantalla de bienvenida + countdown.
// Se entra desde /student/quizzes → "Comenzar" con la asignación real.
// Al pulsar "COMENZAR ▶" corre countdown 3→2→1→¡A jugar! y, al terminar,
// muestra un placeholder claro que 4B reemplazará por el runner real.
// Transición mínima pending → in_progress al pulsar COMENZAR (endpoint
// POST /quiz-assignments/{aid}/start). Idempotente en el backend.

const COUNTDOWN_STEPS = ['3', '2', '1', '¡A jugar!'];

export default function QuizStart() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [asg, setAsg] = useState(null); // null=loading
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('welcome'); // 'welcome' | 'countdown' | 'ready' | 'finished'
  const [countdown, setCountdown] = useState(null);
  const [starting, setStarting] = useState(false);
  // Paso 4C: estado local de la sesión — no persiste todavía (eso será 4D).
  const [currentPosition, setCurrentPosition] = useState(0);
  const [completedActivities, setCompletedActivities] = useState([]); // [{activity_id, position, attempts_used, points_earned, success, elapsed_seconds}]
  const [totalPointsEarned, setTotalPointsEarned] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get(`/quiz-assignments/${aid}`)
      .then(r => {
        if (cancelled) return;
        const d = r.data;
        setAsg(d);
        // Paso 4D — reanudar exactamente donde quedó (fuente de verdad = backend).
        const startPos = Math.max(0, Number(d?.current_activity_position || 0));
        setCurrentPosition(startPos);
        const progRows = Array.isArray(d?.progress) ? d.progress : [];
        setCompletedActivities(progRows.filter(r => r.status === 'completed'));
        setTotalPointsEarned(Number(d?.points_obtained || 0));
        // Si ya está completed, saltar directamente al placeholder final.
        if (d?.status === 'completed') setPhase('finished');
        // Si ya está in_progress (o hay actividades completadas), saltar bienvenida
        // y countdown — el usuario ya inició antes. Aterrizar directo en el runner.
        else if (d?.status === 'in_progress' || progRows.some(p => p.status === 'completed')) setPhase('ready');
      })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || 'No se pudo cargar el Quiz'); });
    return () => { cancelled = true; };
  }, [aid]);

  const runCountdown = useCallback(() => {
    setPhase('countdown');
    let i = 0;
    setCountdown(COUNTDOWN_STEPS[0]);
    try { playChessMoveSound(); } catch { /* silent */ }
    const iv = setInterval(() => {
      i += 1;
      if (i >= COUNTDOWN_STEPS.length) {
        clearInterval(iv);
        setPhase('ready');
        return;
      }
      setCountdown(COUNTDOWN_STEPS[i]);
      try { playChessMoveSound(); } catch { /* silent */ }
    }, 800);
  }, []);

  const handleStart = useCallback(async () => {
    if (starting || phase !== 'welcome') return; // anti-doble-click
    setStarting(true);
    try {
      // Transición mínima pending → in_progress (idempotente server-side).
      // Si falla no bloqueamos el countdown — la mayoría de casos son
      // problemas transitorios; 4D reintenta al registrar el primer intento.
      await api.post(`/quiz-assignments/${aid}/start`).catch(() => {});
      runCountdown();
    } finally {
      // starting queda true durante todo el countdown para evitar re-entrar.
    }
  }, [aid, phase, runCountdown, starting]);

  const back = () => nav('/student/quizzes');

  if (error) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center p-6">
        <div className="max-w-md rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
          <div className="text-4xl mb-2">😕</div>
          <div className="font-display font-extrabold text-lg text-rose-800">No se pudo abrir el Quiz</div>
          <div className="text-sm text-rose-700 mt-1">{error}</div>
          <Button className="mt-4" onClick={back}><ArrowLeft className="w-4 h-4 mr-1"/> Volver</Button>
        </div>
      </div>
    );
  }
  if (!asg) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center text-slate-400">
        <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin"/> Cargando…</div>
      </div>
    );
  }

  const quiz = asg.quiz || {};
  const title = quiz.title || asg.quiz_title || 'Quiz';
  const intro = (quiz.intro_text || '').trim() || '¡Prepárate para poner a prueba lo que has aprendido!';
  const activityCount = (quiz.activity_snapshots || quiz.activity_ids || []).length || asg.activity_count || 0;

  // Paso 4B+4C — Runner con avance automático + scoring + intentos.
  // Fuente: activity_snapshots[currentPosition] (Paso 1B, inmutable).
  const snaps = (quiz.activity_snapshots || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const totalActs = snaps.length || activityCount || 0;
  const maxPointsPerActivity = quiz.max_points_per_activity || 30;
  const maxAttempts = quiz.max_attempts || 3;

  const handleActivityCompleted = async (payload) => {
    // Paso 4D — persistir en backend antes de avanzar.
    try {
      await api.post(`/quiz-assignments/${aid}/progress`, {
        activity_id: payload.activity_id,
        position: payload.position,
        attempts_used: payload.attempts_used,
        points_earned: payload.points_earned,
        selected_index: payload.selected_index,
        elapsed_seconds: payload.elapsed_seconds,
        completed: true,
        success: payload.success,
      });
    } catch {
      toast.error('Hubo un problema guardando tu avance. Inténtalo de nuevo.');
      return; // NO avanzar si falló la persistencia
    }
    setCompletedActivities(prev => [...prev, payload]);
    setTotalPointsEarned(prev => prev + (payload.points_earned || 0));
    setTimeout(() => {
      setCurrentPosition(prev => {
        const next = prev + 1;
        if (next >= totalActs) {
          setPhase('finished');
          // Re-fetch para obtener raw_score/final_score calculados por el backend
          api.get(`/quiz-assignments/${aid}`).then(r => setAsg(r.data)).catch(() => {});
        }
        return next;
      });
    }, 700);
  };

  const handleProgress = async (payload) => {
    try {
      await api.post(`/quiz-assignments/${aid}/progress`, {
        activity_id: payload.activity_id,
        position: payload.position,
        attempts_used: payload.attempts_used,
        points_earned: 0,
        elapsed_seconds: payload.elapsed_seconds,
        completed: false,
        current_fen: payload.current_fen,
        move_history: payload.move_history,
      });
    } catch { /* fallo silencioso — el siguiente movimiento volverá a persistir */ }
  };

  const handleAttemptFailed = async (payload) => {
    // Paso 4D — persistencia parcial (attempts_used con completed=false) para
    // que el alumno pueda salir y reanudar en el mismo intento.
    try {
      await api.post(`/quiz-assignments/${aid}/progress`, {
        activity_id: payload.activity_id,
        position: payload.position,
        attempts_used: payload.attempts_used,
        points_earned: 0,
        selected_index: payload.selected_index,
        elapsed_seconds: payload.elapsed_seconds,
        completed: false,
        current_fen: payload.current_fen,
        move_history: payload.move_history,
      });
    } catch { /* fallo silencioso — reintentaremos en el próximo intento o al completar */ }
  };

  if (phase === 'finished') {
    return (
      <QuizResult
        assignment={asg}
        onContinue={back}
      />
    );
  }

  if (phase === 'ready') {
    const current = snaps[currentPosition];
    const currentActivity = current?.activity || null;
    const type = currentActivity?.type;
    if (currentActivity && type === 'puzzle') {
      // Paso 4D — reanudar attempts_used desde el progreso parcial persistido.
      const parcial = (asg?.progress || []).find(r => r.position === currentPosition && r.status !== 'completed');
      const initialAttemptsUsed = parcial ? Number(parcial.attempts_used || 0) : 0;
      const resumeFen = parcial?.current_fen || currentActivity?.fen || '';
      const resumeMoveHistory = Array.isArray(parcial?.move_history) ? parcial.move_history : [];
      return (
        <QuizPuzzleRunner
          key={`${currentPosition}-${current?.source_activity_id}`}
          activity={{ ...currentActivity, source_activity_id: current?.source_activity_id }}
          position={currentPosition}
          totalActivities={totalActs}
          quizTitle={title}
          maxPoints={maxPointsPerActivity}
          maxAttempts={maxAttempts}
          initialAttemptsUsed={initialAttemptsUsed}
          resumeFen={resumeFen}
          resumeMoveHistory={resumeMoveHistory}
          onCompleted={handleActivityCompleted}
          onAttemptFailed={handleAttemptFailed}
          onProgress={handleProgress}
        />
      );
    }
    // FASE QUIZZES · PASO 5C-2 — Rama play_computer.
    // Reanudación: attempts_used viene de quiz_activity_progress (Paso 4D).
    // NUNCA se consulta /studies/.../play-computer/status ni play_computer_attempts:
    // el Quiz tiene su propia contabilidad de intentos y puntos.
    if (currentActivity && type === 'play_computer') {
      const parcial = (asg?.progress || []).find(r => r.position === currentPosition && r.status !== 'completed');
      const initialAttemptsUsed = parcial ? Number(parcial.attempts_used || 0) : 0;
      return (
        <QuizPlayComputerRunner
          key={`${currentPosition}-${current?.source_activity_id}`}
          activity={{ ...currentActivity, source_activity_id: current?.source_activity_id }}
          position={currentPosition}
          totalActivities={totalActs}
          quizTitle={title}
          maxPoints={maxPointsPerActivity}
          maxAttempts={maxAttempts}
          initialAttemptsUsed={initialAttemptsUsed}
          onCompleted={handleActivityCompleted}
          onAttemptFailed={handleAttemptFailed}
          onProgress={handleProgress}
        />
      );
    }
    // FASE QUIZZES · PASO 5D-2 — Rama capture.
    // Adapter delgado sobre CaptureActivityPlayer. 1 intento por actividad.
    // Puntos proporcionales calculados con maxPoints del Quiz (no × 30).
    if (currentActivity && type === 'capture') {
      const parcial = (asg?.progress || []).find(r => r.position === currentPosition && r.status !== 'completed');
      const initialAttemptsUsed = parcial ? Number(parcial.attempts_used || 0) : 0;
      return (
        <QuizCaptureRunner
          key={`${currentPosition}-${current?.source_activity_id}`}
          activity={{ ...currentActivity, source_activity_id: current?.source_activity_id }}
          position={currentPosition}
          totalActivities={totalActs}
          quizTitle={title}
          maxPoints={maxPointsPerActivity}
          maxAttempts={maxAttempts}
          initialAttemptsUsed={initialAttemptsUsed}
          onCompleted={handleActivityCompleted}
          onAttemptFailed={handleAttemptFailed}
          onProgress={handleProgress}
        />
      );
    }
    // Multiple Choice — runner del alumno (selección de opción, sin mover piezas).
    if (currentActivity && type === 'multiple_choice') {
      const parcial = (asg?.progress || []).find(r => r.position === currentPosition && r.status !== 'completed');
      const initialAttemptsUsed = parcial ? Number(parcial.attempts_used || 0) : 0;
      return (
        <QuizMultipleChoiceRunner
          key={`${currentPosition}-${current?.source_activity_id}`}
          activity={{ ...currentActivity, source_activity_id: current?.source_activity_id }}
          position={currentPosition}
          totalActivities={totalActs}
          quizTitle={title}
          maxPoints={maxPointsPerActivity}
          maxAttempts={maxAttempts}
          initialAttemptsUsed={initialAttemptsUsed}
          onCompleted={handleActivityCompleted}
          onAttemptFailed={handleAttemptFailed}
          onProgress={handleProgress}
        />
      );
    }
    // Stub para tipos aún no cubiertos: NO rompe el Quiz, permite volver.
    return (
      <div data-testid="quiz-start-ready" className="min-h-[calc(100vh-4rem)] grid place-items-center p-6 bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50">
        <div className="max-w-lg text-center">
          <div className="text-6xl mb-3">♟️</div>
          <div className="font-display font-black text-3xl text-slate-900">Actividad de tipo <b>{type || '—'}</b></div>
          <div className="text-slate-600 mt-2">El runner para este tipo de actividad llega en próximos pasos.</div>
          <div data-testid="quiz-start-placeholder-4b" className="mt-5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 inline-block">
            <Sparkles className="w-4 h-4 inline mr-1"/> Runner para <code>{type}</code> pendiente.
          </div>
          <div className="mt-6">
            <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-1"/> Volver a Mis Quizzes</Button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'countdown') {
    return (
      <div data-testid="quiz-start-countdown" className="min-h-[calc(100vh-4rem)] grid place-items-center p-6 bg-gradient-to-br from-indigo-600 via-fuchsia-600 to-rose-500 text-white">
        <div className="text-center">
          <div className="text-sm uppercase tracking-[0.4em] font-semibold opacity-80 mb-3">¡Prepárate!</div>
          <div key={countdown} data-testid="quiz-start-countdown-value" className="font-display font-black text-white leading-none" style={{ fontSize: 'clamp(6rem, 20vw, 16rem)', animation: 'cd-pop 700ms ease-out' }}>
            {countdown}
          </div>
          <style>{`@keyframes cd-pop { 0% { transform: scale(0.4); opacity: 0 } 55% { transform: scale(1.15); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }`}</style>
        </div>
      </div>
    );
  }

  // Welcome
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button data-testid="quiz-start-back" onClick={back} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-6">
          <ArrowLeft className="w-4 h-4"/> Volver a Mis Quizzes
        </button>

        <div data-testid="quiz-start-welcome" className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-10 text-center">
          <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-3xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white grid place-items-center shadow-lg shadow-indigo-500/30 mb-5">
            <Target className="w-10 h-10 sm:w-12 sm:h-12"/>
          </div>
          <h1 data-testid="quiz-start-title" className="font-display text-3xl sm:text-4xl font-black text-slate-900 leading-tight">{title}</h1>
          <p data-testid="quiz-start-intro" className="text-slate-600 mt-3 text-base sm:text-lg">{intro}</p>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-4 py-1.5 text-sm font-semibold">
            <Sparkles className="w-4 h-4"/> <span data-testid="quiz-start-activities">{activityCount} {activityCount === 1 ? 'actividad' : 'actividades'}</span>
          </div>

          <div className="mt-8">
            <Button
              data-testid="quiz-start-btn"
              onClick={handleStart}
              disabled={starting}
              className="h-14 px-10 text-lg bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-700 hover:to-fuchsia-700 text-white shadow-lg shadow-indigo-500/25 rounded-2xl"
            >
              {starting ? <Loader2 className="w-5 h-5 animate-spin mr-2"/> : <Play className="w-5 h-5 mr-2"/>}
              COMENZAR
            </Button>
          </div>

          <div className="mt-6 text-xs text-slate-400">¡Concéntrate y da lo mejor de ti! ♟️</div>
        </div>
      </div>
    </div>
  );
}
